import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from "@mui/material";

// Configuration
const isProduction = window.location.protocol === 'https:';
const MONITORING_SERVER = isProduction 
  ? "wss://152.42.235.70"  // Secure WebSocket via nginx
  : "ws://152.42.235.70:8082";

const ICE_SERVERS = [
  {
    urls: [
      "turns:monitor.thobwabaya.com:5349?transport=tcp",  // TURNS (TLS) - works through strict firewalls
      "turns:monitor.thobwabaya.com:3478?transport=tcp",  // TURNS (TLS) fallback
      "turn:monitor.thobwabaya.com:3478?transport=tcp",   // Regular TURN fallback
      "turn:monitor.thobwabaya.com:3478?transport=udp"    // UDP fallback
    ],
    username: "aischool",
    credential: "schoolmonitor2024"
  },
  {
    urls: "stun:monitor.thobwabaya.com:3478"
  }
];

/**
 * StudentMonitor - Student-side WebRTC component
 * Shares screen/camera with teacher's monitoring system
 */
const StudentMonitor_webrtc = ({ studentId, studentName, formId }) => {
  const [connected, setConnected] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Not connected');
  
  const wsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const videoRef = useRef(null);

  // Connect to monitoring server
  const connectToServer = async () => {
    try {
      setError(null);
      setStatus('Connecting to monitoring server...');
      
      const ws = new WebSocket(`${MONITORING_SERVER}/ws/student/${studentId}`);
      
      ws.onopen = () => {
        console.log('✅ Connected to monitoring server');
        setConnected(true);
        setStatus('Connected - Ready to share screen');
        
        // Register student - send metadata matching server expectations
        ws.send(JSON.stringify({
          name: studentName,
          form: formId,
          school_id: studentId,
          ip: 'webapp-client',
          timestamp: new Date().toISOString()
        }));
      };
      
      ws.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('📩 Received message:', message.type);
          
          await handleSignalingMessage(message);
        } catch (err) {
          console.error('Error handling message:', err);
        }
      };
      
      ws.onerror = (err) => {
        console.error('❌ WebSocket error:', err);
        setError('Connection error');
        setStatus('Connection failed');
      };
      
      ws.onclose = () => {
        console.log('🔌 Disconnected from monitoring server');
        setConnected(false);
        setStreaming(false);
        setStatus('Disconnected');
        stopStreaming();
      };
      
      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to connect:', err);
      setError(err.message);
      setStatus('Connection failed');
    }
  };

  // Handle WebRTC signaling messages
  const handleSignalingMessage = async (message) => {
    const { type, offer, candidate, teacherId } = message;
    
    switch (type) {
      case 'offer':
        console.log('📨 Received offer from teacher:', teacherId);
        await handleOffer(offer, teacherId);
        break;
        
      case 'ice-candidate':
        console.log('🧊 Received ICE candidate');
        if (peerConnectionRef.current && candidate) {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        }
        break;
        
      default:
        console.log('Unknown message type:', type);
    }
  };

  // Handle offer from teacher
  const handleOffer = async (offer, teacherId) => {
    try {
      // Create peer connection if doesn't exist
      if (!peerConnectionRef.current) {
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        
        // Handle ICE candidates
        pc.onicecandidate = (event) => {
          if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: 'ice-candidate',
              candidate: event.candidate,
              teacherId: teacherId
            }));
          }
        };
        
        // Connection state changes
        pc.onconnectionstatechange = () => {
          console.log('📡 Connection state:', pc.connectionState);
          setStatus(`Connection: ${pc.connectionState}`);
          
          if (pc.connectionState === 'connected') {
            setStreaming(true);
            setStatus('Sharing screen with teacher');
          } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
            setStreaming(false);
            setStatus('Connection lost');
          }
        };
        
        peerConnectionRef.current = pc;
      }
      
      const pc = peerConnectionRef.current;
      
      // Set remote description (offer)
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      
      // Get user media (screen + audio)
      if (!localStreamRef.current) {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 15 }
          },
          audio: true
        });
        
        localStreamRef.current = stream;
        
        // Show preview
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        
        // Add tracks to peer connection
        stream.getTracks().forEach(track => {
          pc.addTrack(track, stream);
        });
        
        // Handle stream end
        stream.getVideoTracks()[0].onended = () => {
          console.log('📺 Screen sharing stopped');
          stopStreaming();
        };
      }
      
      // Create and send answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'answer',
          answer: answer,
          teacherId: teacherId
        }));
      }
      
      console.log('✅ Answer sent to teacher');
    } catch (err) {
      console.error('Error handling offer:', err);
      setError('Failed to share screen: ' + err.message);
    }
  };

  // Start screen sharing
  const startSharing = async () => {
    try {
      setError(null);
      
      if (!connected) {
        setError('Please connect to server first');
        return;
      }
      
      // Teacher will send offer, we just wait
      setStatus('Waiting for teacher to start monitoring...');
      
    } catch (err) {
      console.error('Error starting share:', err);
      setError(err.message);
    }
  };

  // Stop streaming
  const stopStreaming = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setStreaming(false);
    setStatus('Stopped sharing');
  };

  // Disconnect
  const disconnect = () => {
    stopStreaming();
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setConnected(false);
    setStatus('Disconnected');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, color: '#1976d2' }}>
        📺 Student Live Monitor
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Student: <strong>{studentName}</strong> ({studentId})
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Form: <strong>{formId}</strong>
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Status:</strong> {status}
      </Alert>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {!connected ? (
          <Button
            variant="contained"
            onClick={connectToServer}
            sx={{ backgroundColor: '#2196f3' }}
          >
            Connect to Server
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              onClick={startSharing}
              disabled={streaming}
              sx={{ backgroundColor: '#4caf50' }}
            >
              {streaming ? 'Sharing...' : 'Ready to Share'}
            </Button>
            <Button
              variant="outlined"
              onClick={disconnect}
              color="error"
            >
              Disconnect
            </Button>
          </>
        )}
      </Box>

      {streaming && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Your Screen Preview:
          </Typography>
          <video
            ref={videoRef}
            autoPlay
            muted
            style={{
              width: '100%',
              maxWidth: '500px',
              border: '2px solid #ddd',
              borderRadius: '8px',
              backgroundColor: '#000'
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            🟢 Your screen is being shared with your teacher
          </Typography>
        </Box>
      )}

      {!connected && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>Instructions:</strong>
          <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li>Click "Connect to Server" to join the monitoring session</li>
            <li>Wait for your teacher to start monitoring</li>
            <li>When prompted, select which screen/window to share</li>
            <li>Your teacher will see your screen in real-time</li>
          </ol>
        </Alert>
      )}
    </Box>
  );
};

export default StudentMonitor_webrtc;
